import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { GIFT_ICON, dollars } from "@/lib/giftIcons";
import { X } from "lucide-react";
import { toast } from "sonner";

export default function GiftMenu({ open, onClose, toUserId, toName, context = "message", onSent }) {
  const [gifts, setGifts] = useState([]);
  const [wallet, setWallet] = useState(0);
  const [sending, setSending] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    if (!open) return;
    api.get("/gifts").then(({ data }) => setGifts(data));
    api.get("/wallet").then(({ data }) => setWallet(data.wallet_balance));
  }, [open]);

  const send = async (gift) => {
    setSending(gift.id);
    try {
      const { data } = await api.post("/gifts/send", { to_user_id: toUserId, gift_id: gift.id, context });
      setWallet(data.wallet_balance);
      toast.success(`You sent a ${gift.name} ✦`);
      onSent && onSent(data);
      onClose();
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("Not enough balance — top up your wallet");
        onClose();
        nav("/wallet");
      } else {
        toast.error(e.response?.data?.detail || "Could not send gift");
      }
    } finally {
      setSending("");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="gift-menu" className="fixed inset-0 z-[9997] flex items-end sm:items-center justify-center bg-ink/80 backdrop-blur-md px-0 sm:px-4"
          onClick={onClose}>
          <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg rounded-t-3xl sm:rounded-3xl border border-white/10 bg-surface p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:p-8 max-h-[88dvh] overflow-y-auto overscroll-contain">
            <span aria-hidden className="sm:hidden block w-10 h-1 rounded-full bg-white/20 mx-auto -mt-1 mb-4" />
            <button data-testid="gift-menu-close" onClick={onClose} className="absolute top-3 right-3 sm:top-5 sm:right-5 w-10 h-10 flex items-center justify-center text-text-muted hover:text-text-main transition-colors"><X size={20} /></button>
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-2">Send a gift</p>
            <h2 className="font-display text-3xl sm:text-4xl font-light mb-1 pr-8">{toName ? `Spoil ${toName}` : "Send a gift"}</h2>
            <p className="text-sm text-text-muted mb-4 sm:mb-6">Wallet balance: <span className="text-text-main">{dollars(wallet)}</span></p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 sm:gap-3">
              {gifts.map((g, i) => {
                const Icon = GIFT_ICON[g.icon] || GIFT_ICON.heart;
                return (
                  <motion.button key={g.id} data-testid={`gift-${g.id}`} disabled={sending === g.id}
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                    whileHover={{ y: -4 }} onClick={() => send(g)}
                    className="group relative flex flex-col items-center gap-2 rounded-2xl border border-white/10 bg-ink/40 p-4 sm:p-5 active:scale-95 hover:border-white/30 transition-colors disabled:opacity-50">
                    <span className="w-14 h-14 rounded-full flex items-center justify-center transition-transform group-hover:scale-110"
                      style={{ background: `${g.color}22`, border: `1px solid ${g.color}66` }}>
                      <Icon size={26} style={{ color: g.color }} strokeWidth={1.5} />
                    </span>
                    <span className="font-display text-xl leading-none">{g.name}</span>
                    <span className="text-xs text-text-muted text-center leading-tight">{g.blurb}</span>
                    <span className="text-sm mt-1" style={{ color: g.color }}>{dollars(g.price)}</span>
                  </motion.button>
                );
              })}
            </div>
            <button data-testid="gift-topup" onClick={() => { onClose(); nav("/wallet"); }}
              className="mt-4 sm:mt-6 w-full py-3.5 sm:py-3 rounded-full border border-white/15 hover:border-gold/50 transition-colors text-xs uppercase tracking-[0.2em] text-text-muted">
              Top up wallet
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
